import {Invoice,Client,BusinessSettings,PaymentRecord,EmailTemplate} from '../types';
import {formatCurrency,formatDate} from './formatters';
import {calculatePaymentTotals} from './calculations';
import {Repository,isPocketBaseMode,loadSettings,verifyLiveBusinessSettings} from '../data/repository';
import {validateAndDedupeRecipients} from './validators';
import {generatePaymentDocumentPdf} from './pdf';
import {dispatchEmailJob,dispatchSmsQueue} from '../config';

interface PaymentReceiptNotificationResult {
  skipped: boolean;
  alreadyQueued: boolean;
  emailJobId?: string;
  emailDispatched: boolean;
  smsDispatchAttempted: boolean;
  smsDispatchSucceeded: boolean;
  emailDispatchError?: string;
  smsDispatchError?: string;
}

const replaceVariables=(text: string,variables: Record<string,string>): string=> {
  let result=text;
  Object.entries(variables).forEach(([key,value])=> {
    result=result.replace(new RegExp(`{{${key}}}`,'g'),value || '');
  });
  return result;
};

const getClientGreetingName=(client: Client): string=> {
  const fullName=String(client.name || '').trim();
  if (!fullName) return 'there';
  return fullName.split(/\s+/)[0];
};

export const getProfessionalInvoiceEmailDraft=(): {subject: string;body: string;}=> ({
  subject: 'Invoice {{invoice_number}} from {{business_name}} | Due {{due_date}}',
  body: `Hi {{client_name}},

Thank you for working with {{business_name}}.

Please find attached invoice {{invoice_number}} for {{invoice_total}}.

Invoice summary:
Invoice number: {{invoice_number}}
Amount due: {{balance_due}}
Due date: {{due_date}}

If you have any questions regarding this invoice or payment arrangements,please contact:

{{accounts_contact_signature}}

Kind regards,
{{accounts_contact_name}}
{{accounts_contact_title}}
{{business_name}}`
});

export const renderEmailTemplate=(
  template: {subject: string;body: string},
  invoice: Invoice,
  client: Client,
  businessSettings: BusinessSettings,
  payments: PaymentRecord[],
  jobName?: string
): {subject: string;body: string}=> {
  const {totalPaid}=calculatePaymentTotals(payments);
  const invoiceDefaults=Repository.settings.getInvoiceDefaults();
  const automation=Repository.settings.getAutomation();
  const accountsContactName=automation.accountsContactName?.trim() || 'Accounts Team';
  const accountsContactTitle=automation.accountsContactTitle?.trim() || 'Accounts';
  const accountsContactEmail=automation.accountsContactEmail?.trim() || automation.replyToEmail?.trim() || businessSettings.email || '';
  const accountsContactPhone=automation.accountsContactPhone?.trim() || businessSettings.phone || '';
  const accountsContactSignature=[
    `${accountsContactName}${accountsContactTitle ? ` - ${accountsContactTitle}` : ''}`,
    accountsContactEmail,
    accountsContactPhone
  ].filter(Boolean).join('\n');

  const variables={
    client_name: getClientGreetingName(client),
    client_full_name: String(client.name || client.companyName || '').trim(),
    business_name: businessSettings.businessName,
    invoice_number: invoice.invoiceNumber,
    invoice_total: formatCurrency(invoice.total,invoice.currency),
    amount_paid: formatCurrency(totalPaid,invoice.currency),
    balance_due: formatCurrency(invoice.balanceDue,invoice.currency),
    due_date: formatDate(invoice.dueDate),
    payment_terms: invoiceDefaults.paymentTerms,
    job_name: jobName || '',
    service_period: '',
    invoice_link: `#/invoices/view/${invoice.id}`,
    accounts_contact_name: accountsContactName,
    accounts_contact_title: accountsContactTitle,
    accounts_contact_email: accountsContactEmail,
    accounts_contact_phone: accountsContactPhone,
    accounts_contact_signature: accountsContactSignature,
  };

  return {
    subject: replaceVariables(template.subject,variables),
    body: replaceVariables(template.body,variables)
  };
};

export const getDefaultEmailTemplates=(): Array<{id: string;name: string;templateType: string;subject: string;body: string;isActive: boolean;}>=> [
  {id: 'tmpl_invoice_sent',name: 'Invoice Sent',templateType: 'invoice_sent',subject: 'New Invoice {{invoice_number}} from {{business_name}}',body: 'Hi {{client_name}},\n\nPlease find attached invoice {{invoice_number}} for {{invoice_total}}.\n\nDue date: {{due_date}}\nBalance due: {{balance_due}}\n\nThank you for your business!\n\n{{business_name}}',isActive: true},
  {id: 'tmpl_payment_receipt',name: 'Payment Receipt',templateType: 'payment_receipt',subject: 'Payment Received - Invoice {{invoice_number}}',body: 'Hi {{client_name}},\n\nThank you for your payment of {{amount_paid}} for invoice {{invoice_number}}.\n\nCurrent balance: {{balance_due}}\n\nRegards,\n{{business_name}}',isActive: true},
  {id: 'tmpl_deposit_receipt',name: 'Deposit Receipt',templateType: 'deposit_receipt',subject: 'Deposit Received - Invoice {{invoice_number}}',body: 'Hi {{client_name}},\n\nThank you for your deposit of {{amount_paid}} for invoice {{invoice_number}}.\n\nRemaining balance: {{balance_due}}\n\nRegards,\n{{business_name}}',isActive: true},
  {id: 'tmpl_part_payment_receipt',name: 'Part Payment Receipt',templateType: 'part_payment_receipt',subject: 'Part Payment Received - Invoice {{invoice_number}}',body: 'Hi {{client_name}},\n\nThank you for your part payment of {{amount_paid}} for invoice {{invoice_number}}.\n\nRemaining balance: {{balance_due}}\n\nRegards,\n{{business_name}}',isActive: true},
  {id: 'tmpl_payment_reminder',name: 'Payment Reminder',templateType: 'payment_reminder',subject: 'Reminder: Invoice {{invoice_number}} is due',body: 'Hi {{client_name}},\n\nThis is a friendly reminder that invoice {{invoice_number}} for {{invoice_total}} is due on {{due_date}}.\n\nCurrent balance: {{balance_due}}\n\nPlease arrange payment at your earliest convenience.\n\nThank you,\n{{business_name}}',isActive: true},
  {id: 'tmpl_overdue_invoice',name: 'Overdue Invoice',templateType: 'overdue_invoice',subject: 'Overdue: Invoice {{invoice_number}}',body: 'Hi {{client_name}},\n\nInvoice {{invoice_number}} for {{invoice_total}} is now overdue.\n\nBalance due: {{balance_due}}\n\nPlease arrange immediate payment to avoid late fees.\n\n{{business_name}}',isActive: true},
  {id: 'tmpl_recurring_invoice',name: 'Recurring Invoice',templateType: 'recurring_invoice',subject: 'Recurring Invoice {{invoice_number}} from {{business_name}}',body: 'Hi {{client_name}},\n\nYour recurring invoice {{invoice_number}} for {{invoice_total}} is ready.\n\nBalance due: {{balance_due}}\n\nDue date: {{due_date}}\n\nThank you for your continued business!\n\n{{business_name}}',isActive: true},
  {id: 'tmpl_subscription_renewal',name: 'Subscription Renewal',templateType: 'subscription_renewal',subject: 'Subscription Renewal - Invoice {{invoice_number}}',body: 'Hi {{client_name}},\n\nYour subscription renewal invoice {{invoice_number}} for {{invoice_total}} is ready.\n\nBalance due: {{balance_due}}\n\nDue date: {{due_date}}\n\nThank you for your continued business!\n\n{{business_name}}',isActive: true}
];

export const queueAutomaticPaymentReceiptNotification=async (params: {invoice: Invoice;payment: PaymentRecord;previousBalance: number;balanceAfterPayment: number;}): Promise<PaymentReceiptNotificationResult>=> {
  const {invoice,payment,previousBalance,balanceAfterPayment}=params;

  // Skip if payment type is not eligible for automatic receipt
  if (!['deposit','part_payment','final_payment'].includes(payment.type)) {
    return {skipped: true,alreadyQueued: false,emailDispatched: false,smsDispatchAttempted: false,smsDispatchSucceeded: false};
  }

  // Load and verify settings
  await loadSettings();
  if (!verifyLiveBusinessSettings()) {
    throw new Error('Business settings could not be verified. Use Receipt / Advice from Payment History after correcting Settings.');
  }

  // Load required data
  const [clients,contacts,templates,jobs,timelineEvents]=await Promise.all([
    Repository.clients.getAll(),
    Repository.contacts.getAll(),
    Repository.emailTemplates.getAll(),
    Repository.jobs.getAll(),
    Repository.timeline.getAll()
  ]);

  // Idempotency guard: check if receipt already queued for this payment
  const existingReceiptEvent=timelineEvents.find(event=> 
    event.entityType==='payment' && 
    event.entityId===payment.id && 
    event.eventType==='payment_receipt_email_queued'
  );
  if (existingReceiptEvent) {
    return {skipped: false,alreadyQueued: true,emailDispatched: false,smsDispatchAttempted: false,smsDispatchSucceeded: false};
  }

  // Find client
  const client=clients.find(c=> c.id===invoice.clientId);
  if (!client) {
    throw new Error('Client record could not be found for this invoice.');
  }

  // Read automation settings
  const business=Repository.settings.getBusiness();
  const invoiceDefaults=Repository.settings.getInvoiceDefaults();
  const automation=Repository.settings.getAutomation();

  // Build recipients using automation rules
  const to=automation.recipientBehavior?.useClientBilling !==false 
    ? [client.email || '',...String(client.additionalEmails || '').split(',').map(e=> e.trim()).filter(Boolean)] 
    : [];
  const cc=[
    ...(automation.recipientBehavior?.includeContacts !==false 
      ? contacts.filter(c=> c.clientId===invoice.clientId && c.includeOnInvoices===true).map(c=> c.email || '').filter(Boolean) 
      : []),
    ...String(automation.defaultCc || '').split(',').map(e=> e.trim()).filter(Boolean),
    ...String(automation.permanentAuditCc || '').split(',').map(e=> e.trim()).filter(Boolean)
  ];
  const bcc=[
    ...String(automation.defaultBcc || '').split(',').map(e=> e.trim()).filter(Boolean),
    ...String(automation.permanentAuditBcc || '').split(',').map(e=> e.trim()).filter(Boolean)
  ];

  // Validate and dedupe recipients
  const validation=validateAndDedupeRecipients(to,cc,bcc);
  if (!validation.isValid) {
    throw new Error(validation.error || 'Invalid recipient email address.');
  }

  // Select appropriate template
  let selectedTemplate: EmailTemplate | undefined=templates.find(t=> {
    if (payment.type==='deposit') return t.templateType==='deposit_receipt' && t.isActive;
    if (payment.type==='part_payment') return t.templateType==='part_payment_receipt' && t.isActive;
    if (payment.type==='final_payment') return t.templateType==='payment_receipt' && t.isActive;
    return false;
  });

  // Fallback template if no active match found
  if (!selectedTemplate) {
    selectedTemplate={
      id: 'tmpl_payment_receipt_fallback',
      name: 'Payment Receipt',
      templateType: 'payment_receipt',
      subject: 'Payment Receipt - Invoice {{invoice_number}}',
      body: `Hi {{client_name}},\n\nThank you for your payment of {{amount_paid}} for invoice {{invoice_number}}.\n\nRemaining balance: {{balance_due}}\n\nPlease find your payment receipt attached.\n\nKind regards,\n{{business_name}}`,
      isActive: true
    };
  }

  // Build document invoice with updated balance
  const documentInvoice={...invoice,balanceDue: balanceAfterPayment};
  const renderClient={...client,name: client.name?.trim() || client.companyName?.trim() || 'Customer'};
  const job=jobs.find(j=> j.id===invoice.jobId);

  // Render template - passing [payment] so {{amount_paid}} shows the new payment only
  const rendered=renderEmailTemplate(
    selectedTemplate,
    documentInvoice,
    renderClient,
    business,
    [payment],
    job?.jobName
  );

  // Generate PDF exactly once
  const pdf=await generatePaymentDocumentPdf(
    documentInvoice,
    client,
    payment,
    previousBalance,
    'payment_receipt',
    business,
    invoiceDefaults
  );

  // Create PDF snapshot
  const snapshot=await Repository.pdfSnapshots.create({
    invoiceId: invoice.id,
    invoiceNumber: invoice.invoiceNumber,
    exportedAt: new Date().toISOString(),
    invoiceTotal: invoice.total,
    balanceDue: balanceAfterPayment,
    pdfFilename: pdf.filename,
    fileReference: pdf.filename,
    file: isPocketBaseMode() ? new File([pdf.pdfBlob],pdf.filename,{type: 'application/pdf'}) : undefined,
    notesSnapshot: `payment_receipt for payment ${payment.id}`,
    paymentSummarySnapshot: `Previous balance: ${formatCurrency(previousBalance,invoice.currency)};Payment: ${formatCurrency(payment.amount,invoice.currency)};Remaining balance: ${formatCurrency(balanceAfterPayment,invoice.currency)}`
  });

  // Create email job
  const emailJob=await Repository.emailJobs.create({
    invoiceId: invoice.id,
    clientId: invoice.clientId,
    jobId: invoice.jobId || '',
    toRecipients: validation.to,
    ccRecipients: validation.cc,
    bccRecipients: validation.bcc,
    subject: rendered.subject,
    body: rendered.body,
    templateType: 'payment_receipt',
    pdfSnapshotId: snapshot.id,
    selectedAttachmentIds: [],
    attachmentNamesSnapshot: [pdf.filename],
    attachmentCount: 1,
    hasInternalOnlyAttachmentWarning: false,
    status: 'queued',
    priority: 1,
    retryCount: 0,
    createdAt: new Date().toISOString()
  });

  // Create timeline event
  await Repository.timeline.create({
    entityType: 'payment',
    entityId: payment.id,
    eventType: 'payment_receipt_email_queued',
    description: `Automatic payment receipt queued for payment ${payment.id} (invoice ${invoice.invoiceNumber}). Email job: ${emailJob.id}`,
    createdAt: new Date().toISOString(),
    createdBy: 'user'
  });

  // Attempt immediate email dispatch
  let emailDispatched=false;
  let emailDispatchError: string | undefined;
  let smsDispatchAttempted=false;
  let smsDispatchSucceeded=false;
  let smsDispatchError: string | undefined;

  try {
    const relayResult=await dispatchEmailJob(emailJob.id);
    if (relayResult.success) {
      emailDispatched=true;

      // Attempt SMS only after successful email
      smsDispatchAttempted=true;
      try {
        await dispatchSmsQueue();
        smsDispatchSucceeded=true;
      } catch (smsError: any) {
        smsDispatchError=smsError.message || 'SMS processing failed';
        // Do not throw - SMS is best-effort only
      }
    } else {
      emailDispatchError=relayResult.message || 'Email relay did not process immediately';
    }
  } catch (emailError: any) {
    emailDispatchError=emailError.message || 'Email dispatch failed';
    // Keep email job queued - do not throw
  }

  return {
    skipped: false,
    alreadyQueued: false,
    emailJobId: emailJob.id,
    emailDispatched,
    smsDispatchAttempted,
    smsDispatchSucceeded,
    emailDispatchError,
    smsDispatchError
  };
};